import { useEffect, useRef, useState } from "react";
import { SlidersHorizontal, Check, CheckCheck, ChevronRight, X } from "lucide-react";
import { TOOL_LINE_COLORS } from "@/lib/format";
import { GroupIcon } from "@/lib/groupIcons";

const engineKey = (e) => `${e.tool}|${e.engine}`;

// 슈퍼그룹(도구 그룹) → 엔진의 2단 트리 필터. 그룹 행을 클릭하면 그 그룹 전체를 선택/해제,
// 화살표를 클릭하면 펼쳐서 엔진 단위로 개별 선택 가능 — "필터링·슈퍼그룹에서 들어가는 것도
// 편하게" 요청 반영(2026-07-16). groups: [{superGroup, label, engines: [{tool,engine,label}]}].
// extraOptions: 그룹 트리에 안 들어가는 낱개 옵션(예: "미선택" — 알려진 조합 밖의 사용량) —
// 그룹 목록 아래에 구분선과 함께 평면 토글로 붙는다(2026-07-17, 전체 사용자 탭 필터 통일).
export default function EngineGroupFilterDropdown({
  groups, selected, expandedGroups, extraOptions = [],
  onToggleEngine, onToggleGroup, onToggleExpand, onSelectAll, onClear,
  disabled = false, disabledTitle,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const isSelected = (e) => selected.some((s) => engineKey(s) === engineKey(e));
  const count = selected.length;
  const allEngines = groups.flatMap((g) => g.engines).concat(extraOptions);
  const allSelected = allEngines.length > 0 && allEngines.every(isSelected);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          count > 0 ? "bg-brand-50 text-brand-700" : "bg-ink-50 text-ink-600 hover:bg-ink-100"
        }`}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        도구 필터
        {count > 0 && (
          <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">{count}</span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-10 mt-1.5 max-h-96 w-72 overflow-y-auto rounded-xl bg-white p-1.5 shadow-[0_4px_12px_rgba(15,23,42,0.12)]">
          {(!allSelected || count > 0) && (
            <div className="mb-1 flex items-center gap-1">
              {!allSelected && (
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="flex flex-1 items-center gap-1.5 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-ink-500 hover:bg-ink-50"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  전체 선택
                </button>
              )}
              {count > 0 && (
                <button
                  type="button"
                  onClick={onClear}
                  className="flex flex-1 items-center gap-1.5 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-ink-500 hover:bg-ink-50"
                >
                  <X className="h-3.5 w-3.5" />
                  전체 해제
                </button>
              )}
            </div>
          )}

          {groups.map((g) => {
            const selectedInGroup = g.engines.filter(isSelected);
            const groupAllSelected = selectedInGroup.length === g.engines.length;
            const groupNoneSelected = selectedInGroup.length === 0;
            const expanded = expandedGroups.has(g.superGroup);
            const dotColor = TOOL_LINE_COLORS[g.superGroup] || "#94a3b8";
            return (
              <div key={g.superGroup}>
                <div
                  className={`flex w-full items-center gap-1.5 rounded-lg pr-2.5 text-left text-sm ${
                    groupAllSelected ? "bg-brand-50 text-brand-700" : groupNoneSelected ? "text-ink-700 hover:bg-ink-50" : "text-ink-700"
                  }`}
                >
                  {g.engines.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => onToggleExpand(g.superGroup)}
                      className="shrink-0 rounded p-2 hover:bg-ink-100"
                      aria-label={expanded ? "접기" : "펼치기"}
                    >
                      <ChevronRight className={`h-3.5 w-3.5 text-ink-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
                    </button>
                  ) : (
                    <span className="w-[1.625rem] shrink-0" />
                  )}
                  <button
                    type="button"
                    onClick={() => onToggleGroup(g.superGroup, g.engines)}
                    className="flex flex-1 items-center gap-2 py-2"
                  >
                    <GroupIcon superGroup={g.superGroup} color={dotColor} className="h-3.5 w-3.5" />
                    <span className="flex-1">{g.label}</span>
                    {!groupNoneSelected && (
                      <Check className={`h-3.5 w-3.5 shrink-0 ${groupAllSelected ? "" : "opacity-40"}`} />
                    )}
                  </button>
                </div>
                {expanded && g.engines.map((e) => {
                  const active = isSelected(e);
                  return (
                    <button
                      key={engineKey(e)}
                      type="button"
                      onClick={() => onToggleEngine(e)}
                      className={`flex w-full items-center gap-2 rounded-lg py-2 pl-9 pr-2.5 text-left text-sm ${
                        active ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-50"
                      }`}
                    >
                      <span className="flex-1">{e.label}</span>
                      {active && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {extraOptions.length > 0 && (
            <div className="mt-1 border-t border-ink-100 pt-1">
              {extraOptions.map((e) => {
                const active = isSelected(e);
                return (
                  <button
                    key={engineKey(e)}
                    type="button"
                    onClick={() => onToggleEngine(e)}
                    className={`flex w-full items-center gap-2 rounded-lg py-2 pl-2.5 pr-2.5 text-left text-sm ${
                      active ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-50"
                    }`}
                  >
                    <span className="flex-1">{e.label}</span>
                    {active && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
